import { useState, useEffect } from "react";
import { connect } from "react-redux";

import Loader from "src/components/Loader";
import { fetchSubscription, createSubscriptionMapping } from "src/api/pegasus";
import type { Location } from "src/types";

import Header from "./Header";
import InfoBanner from "./InfoBanner";
import AlertMessage from "./AlertMessage";
import SubscriptionDetails from "./SubscriptionDetails";
import OnboardButton from "./OnboardButton";
import Footer from "./Footer";
import { formatStatusForDisplay, getButtonConfig } from "./utils";
import { Flex, Text } from "@unifize/sarah";
import {
  SubscriptionData,
  SubscriptionStatus,
  type SubscriptionStatusType,
} from "./types";

type Props = {
  location: Location;
};

const Pegasus = ({ location }: Props) => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState(false);
  const [onboardSuccess, setOnboardSuccess] = useState(false);
  const [onboardError, setOnboardError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatusType | null>(null);
  const [onboardMessage, setOnboardMessage] = useState<string | null>(null);

  // Get token from query params (Azure Marketplace typically passes this as 'token')
  const purchaseIdToken =
    (location.query && (location.query as { token: string }).token) ||
    new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!purchaseIdToken) {
      setError(
        "Missing token. Please access this page from Azure Marketplace.",
      );
      setLoading(false);
      return;
    }

    // Fetch subscription details
    async function fetchSubscriptionDetails() {
      try {
        const data = await fetchSubscription(purchaseIdToken as string);
        setSubscription(data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchSubscriptionDetails();
  }, [purchaseIdToken]);

  const handleOnboard = async () => {
    if (!subscription || !purchaseIdToken) return;

    setOnboarding(true);
    setOnboardError(null);
    setOnboardMessage(null);

    try {
      const result = await createSubscriptionMapping(
        purchaseIdToken,
        subscription,
      );

      // Get the subscription status from the response
      const status = result["subscription-status"];

      // Determine if it's a success or failure based on status
      const isSuccess =
        status === SubscriptionStatus.REQUEST_CREATED ||
        status === SubscriptionStatus.NOT_STARTED ||
        status === SubscriptionStatus.STARTED;

      if (isSuccess) {
        setOnboardSuccess(true);
        setSubscriptionStatus(status);

        // Set appropriate message based on status
        if (status === SubscriptionStatus.REQUEST_CREATED) {
          setOnboardMessage(
            "Onboarding request created successfully. Our team will contact you shortly.",
          );
        } else if (status === SubscriptionStatus.NOT_STARTED) {
          setOnboardMessage(
            "We're getting everything ready for you! Your onboarding will start shortly.",
          );
        } else if (status === SubscriptionStatus.STARTED) {
          setOnboardMessage(
            "Your subscription has started. You can now access your workspace.",
          );
        }
      } else {
        // Failure statuses: SUSPENDED, CANCELLED
        setOnboardSuccess(false);
        setSubscriptionStatus(status);
        setOnboardError(
          status === SubscriptionStatus.SUSPENDED
            ? "Your subscription has been suspended. Please contact support."
            : "Your subscription has been cancelled. Please contact support.",
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to onboard subscription";
      setOnboardError(errorMessage);
      setOnboardSuccess(false);
      setSubscriptionStatus(null);
    } finally {
      setOnboarding(false);
    }
  };

  const getCurrentStatus = (): string => {
    return subscriptionStatus || "pending_activation";
  };

  if (loading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  const currentStatus = getCurrentStatus();
  const buttonConfig = getButtonConfig(
    currentStatus,
    onboarding,
    loading,
    subscription,
    onboardSuccess,
    subscriptionStatus,
  );

  return (
    <div>
      <div
        style={{
          height: "100vh",
          backgroundColor: "white",
          padding: "2rem",
          maxWidth: "1280px",
          margin: "0 auto",
          overflowY: "scroll",
        }}
      >
        <Header />
        <InfoBanner />

        {onboardSuccess && onboardMessage && (
          <AlertMessage type="success" message={onboardMessage} />
        )}

        {error && <AlertMessage type="error" message={error} />}

        {onboardError && <AlertMessage type="error" message={onboardError} />}

        {subscription ? (
          <SubscriptionDetails
            subscription={subscription}
            currentStatus={currentStatus}
            formatStatusForDisplay={formatStatusForDisplay}
          />
        ) : (
          <Flex padding="6" items="center" justify="center">
            <Text variant="body" weight="regular" color="#4B5563">
              {error
                ? "Unable to load subscription details."
                : "No subscription data available."}
            </Text>
          </Flex>
        )}

        {!error && !onboardError && (
          <OnboardButton
            onClick={handleOnboard}
            disabled={buttonConfig.disabled}
            text={buttonConfig.text}
          />
        )}

        <Footer />
      </div>
    </div>
  );
};

const mapStateToProps = ({ location }: { location: Location }) => ({
  location,
});

export default connect(mapStateToProps)(Pegasus);
